import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { H3, Text, Button } from '@/components/atoms'
import { BenefitCard, Slider } from '@/components/molecules'
import airvet from '@/shared/assets/images/onboarding/airvet.png'
import airvetMobile from '@/shared/assets/images/onboarding/airvet-mobile.png'
import wellfold from '@/shared/assets/images/onboarding/wellfold.png'
import wellfoldMobile from '@/shared/assets/images/onboarding/wellfold-mobile.png'
import directvet from '@/shared/assets/images/onboarding/directvet.png'
import directvetMobile from '@/shared/assets/images/onboarding/directvet-mobile.png'
import pharmacy from '@/shared/assets/images/onboarding/pharmacy.png'
import pharmacyMobile from '@/shared/assets/images/onboarding/pharmacy-mobile.png'

type BenefitItem = {
  title: string
  description: ReactNode
  image: string
  imageMobile: string
}

export function PetStepFourPage({
  onBack,
  onContinue,
}: {
  onBack: () => void
  onContinue: () => void
}) {
  const { t } = useTranslation()

  const benefits: BenefitItem[] = [
    {
      title: t('onboarding.pet.step4.benefits.airvet.title'),
      description: (
        <>
          {t('onboarding.pet.step4.benefits.airvet.before')}{' '}
          <span className="text-indigo-base">
            {t('onboarding.pet.step4.benefits.airvet.highlight')}
          </span>{' '}
          {t('onboarding.pet.step4.benefits.airvet.after')}
        </>
      ),
      image: airvet,
      imageMobile: airvetMobile,
    },
    {
      title: t('onboarding.pet.step4.benefits.wellfold.title'),
      description: (
        <>
          <span className="text-indigo-base">
            {t('onboarding.pet.step4.benefits.wellfold.highlight')}
          </span>{' '}
          {t('onboarding.pet.step4.benefits.wellfold.after')}
        </>
      ),
      image: wellfold,
      imageMobile: wellfoldMobile,
    },
    {
      title: t('onboarding.pet.step4.benefits.directVet.title'),
      description: (
        <>
          {t('onboarding.pet.step4.benefits.directVet.before')}{' '}
          <span className="text-indigo-base">
            {t('onboarding.pet.step4.benefits.directVet.highlight')}
          </span>{' '}
          {t('onboarding.pet.step4.benefits.directVet.after')}
        </>
      ),
      image: directvet,
      imageMobile: directvetMobile,
    },
    {
      title: t('onboarding.pet.step4.benefits.pharmacy.title'),
      description: (
        <>
          <span className="text-indigo-base">
            {t('onboarding.pet.step4.benefits.pharmacy.highlight')}
          </span>{' '}
          {t('onboarding.pet.step4.benefits.pharmacy.after')}
        </>
      ),
      image: pharmacy,
      imageMobile: pharmacyMobile,
    },
  ]

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 md:flex md:flex-row md:justify-between md:pb-0">
        <div className="flex flex-col justify-between gap-4 md:max-w-89.5 md:pt-6">
          <div className="flex flex-col gap-4 md:gap-6">
            <div className="flex flex-col gap-4 md:gap-6">
              <H3>{t('onboarding.pet.step4.title')}</H3>
              <Text className="text-neutral-600">
                {t('onboarding.pet.step4.body')}
              </Text>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 md:hidden">
              {benefits.map((benefit) => (
                <BenefitCard
                  key={benefit.title}
                  image={
                    <img
                      src={benefit.imageMobile}
                      alt={benefit.title}
                      className="h-full w-full object-cover"
                    />
                  }
                  title={benefit.title}
                  description={benefit.description}
                  className="w-71 shrink-0"
                />
              ))}
            </div>
          </div>
          <div className="hidden gap-3 md:mb-2.5 md:flex">
            <Button
              className="h-12"
              variant="secondary"
              onClick={onBack}
              data-testid="step-four-back"
            >
              {t('onboarding.pet.back')}
            </Button>
            <Button
              className="h-12"
              onClick={onContinue}
              data-testid="step-four-continue"
            >
              {t('onboarding.pet.continue')}
            </Button>
          </div>
        </div>

        <div className="hidden md:block md:max-w-99.5">
          <Slider
            items={benefits}
            renderItem={(item) => (
              <BenefitCard
                image={
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover"
                  />
                }
                title={item.title}
                description={item.description}
              />
            )}
            layout="list"
          />
        </div>
      </div>

      <div className="flex w-full gap-3 pt-4 md:hidden">
        <Button
          className="h-12 w-full"
          variant="secondary"
          onClick={onBack}
          data-testid="step-four-back"
        >
          {t('onboarding.pet.back')}
        </Button>
        <Button
          className="h-12 w-full"
          onClick={onContinue}
          data-testid="step-four-continue"
        >
          {t('onboarding.pet.continue')}
        </Button>
      </div>
    </div>
  )
}
